import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination, Autoplay } from "swiper/modules";
import { Link } from "react-router-dom";
import BASE_URL from "Utilities/BASE_URL";
import SliderNav from "Components/SliderNav/SliderNav";
import { getCourseInstructors, leadPlusLabel } from "Utilities/courseInstructors";
import "./LatestCoursesStyle.css";

const getCourseThumbnail = (course) => {
  if (course?.trailer?.assets?.thumbnail)
    return course.trailer.assets.thumbnail;
  if (course?.thumbnail) return course.thumbnail;
  if (course?.image) return `${BASE_URL.replace(/\/api$/, "")}/${course.image}`;
  return null;
};

const LatestCourses = ({ title, data }) => {
  const { i18n } = useTranslation();
  const [swiperInstance, setSwiperInstance] = useState(null);
  const isRTL = i18n.language === "ar";

  if (!data?.length) return null;

  return (
    <div className="flex flex-col items-center small_screen_container px-2 md:px-4 overflow-hidden">
      <div className="w-full mb-section-title-gap lg:mb-section-title-gap-lg">
        <SliderNav
          title={title}
          onPrev={() => swiperInstance?.slidePrev()}
          onNext={() => swiperInstance?.slideNext()}
        />
      </div>

      {/*
        Slider.

        Mobile (<576px): slidesPerView=1, centeredSlides=false — a single slide
        already fills the viewport, and combining centeredSlides with loop+RTL
        was producing a wrong initial offset. Centering kicks in from 576px up,
        where neighbor slides peek and actually need it.

        We use `rewind` instead of `loop` because loop-mode clones inflated
        `.swiper-wrapper` width (e.g. 3312px for 6 slides) and pushed the first
        slide off the leading edge in RTL. Rewind keeps the wrapper at exactly
        `data.length × slideWidth` and gives the same "snap back to first slide
        after the last" UX without any DOM cloning.
      */}
      <Swiper
        key={i18n.language}
        dir={isRTL ? "rtl" : "ltr"}
        onSwiper={setSwiperInstance}
        slidesPerView={1}
        centeredSlides={false}
        spaceBetween={0}
        modules={[Pagination, Autoplay]}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        pagination={{ clickable: true, dynamicBullets: true }}
        breakpoints={{
          576: { slidesPerView: 1.2, centeredSlides: true },
          768: { slidesPerView: 1.4, centeredSlides: true },
          992: { slidesPerView: 1.6, centeredSlides: true },
          1200: { slidesPerView: 2.4, centeredSlides: true },
          1440: { slidesPerView: 3, centeredSlides: true },
        }}
        rewind={data.length > 1}
        observer={true}
        observeParents={true}
        className="gallery-slider w-full"
      >
        {data.map((course, index) => (
          <SwiperSlide key={course._id || index} className="py-3 lg:py-4">
            <Link to={`/course/${course.slug}`} className="block">
              {/*
                Scale/opacity is driven by the `.swiper-slide-active` CSS class
                (see LatestCoursesStyle.css). React can't manage this via state
                because `loop` mode clones the slide DOM nodes; the clones are
                outside React's tree and would show stale styling.
              */}
              <div className="gallery-card relative overflow-hidden rounded-[21px] h-[220px] sm:h-[260px] md:h-[290px] lg:h-[320px] bg-black transition-all duration-500">
                <img
                  className="w-full h-full object-cover"
                  src={getCourseThumbnail(course)}
                  alt={course.name}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                  <span className="text-white text-lg sm:text-xl md:text-2xl font-bold block">
                    {course.name}
                  </span>
                  <span className="text-white/70 text-sm sm:text-base md:text-lg font-medium mt-1 block">
                    {leadPlusLabel(getCourseInstructors(course)) ||
                      course.instructor_profile?.name}
                  </span>
                </div>
              </div>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default LatestCourses;
